import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { FinalcutProgressData, FinalcutQueueData } from 'services/sse.service';
import { anyProcessingFinalCuts as anyProcessingFinalCutsService } from 'services/finalcut.service';
import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { isFcStatusCompleted } from 'components/app/_helpers/_app.helper';
import { fetchStorageSize, fetchFinalcuts } from 'components/app/_mediator/app-data.slice';
import { fetchFinalCut } from './fc-data.slice';
import { FcStallProgress, parseFcStallProgress } from '../_helpers/fc-stall-progress.helper';

interface State {
  fcProgress: Record<FinalcutId, number>;
  fcStallProgress: Record<FinalcutId, FcStallProgress>;
  fcQueue: Record<FinalcutId, number>;
}

const initialState: State = {
  fcProgress: {},
  fcStallProgress: {},
  fcQueue: {},
};

// T H U N K S

const SLICE_ID = 'fc__renderServer';

export const handleProcessingFc = createSafeAsyncThunk(
  SLICE_ID + '/handleProcessingFc',
  async (payload: FinalcutProgressData, { getState, dispatch }) => {
    const { finalCutId, status, percent, data, stepElapsedSeconds, errorMessage } = payload;
    const state = getState() as RootState;
    const { selectedFinalcut, selectedStory } = state.projects;
    const { finalcuts } = state.app__data;

    const stallProgress = parseFcStallProgress(data, stepElapsedSeconds);
    if (stallProgress) {
      dispatch(slice.actions.setFinalcutStallProgress({ finalCutId, ...stallProgress }));
    } else if (typeof percent === 'number') {
      // Percent without stall fields = stall ended (or normal progress). Backend re-sends
      // percent after a stall even when the integer value is unchanged.
      dispatch(slice.actions.clearFinalcutStallProgress(finalCutId));
    }
    if (typeof percent === 'number') {
      dispatch(slice.actions.setFinalcutProgress({ finalCutId, value: percent }));
      dispatch(slice.actions.clearFinalcutQueue(finalCutId));
    }
    if (status && isFcStatusCompleted(status)) {
      dispatch(fetchStorageSize());
      const isSelectedStory = !!finalcuts.find((el) => el.id === finalCutId);
      if (isSelectedStory) dispatch(fetchFinalcuts(selectedStory!));
      if (errorMessage) toast.error(`Finalcut rendering error: ${payload.errorMessage}`);
      if (selectedFinalcut === finalCutId) dispatch(fetchFinalCut(finalCutId));
      if (!(await dispatch(anyProcessingFinalCuts()).unwrap())) return { closeSse: true };
    }
  }
);

export const anyProcessingFinalCuts = createSafeAsyncThunk(SLICE_ID + '/anyProcessingFinalCuts', async () => {
  const res = await anyProcessingFinalCutsService();
  return res.data;
});

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setFinalcutQueue: (state, action: PayloadAction<FinalcutQueueData[]>) => {
      const payload: State['fcQueue'] = {};
      action.payload.forEach((data) => {
        payload[data.finalCutId] = data.positionInQueue;
      });
      state.fcQueue = payload;
    },
    setFinalcutProgress: (state, action: PayloadAction<{ finalCutId: FinalcutId; value: number }>) => {
      const { finalCutId, value } = action.payload;
      state.fcProgress[finalCutId] = value;
    },
    setFinalcutStallProgress: (state, action: PayloadAction<{ finalCutId: FinalcutId } & FcStallProgress>) => {
      const { finalCutId, ...stallProgress } = action.payload;
      state.fcStallProgress[finalCutId] = stallProgress;
    },
    clearFinalcutStallProgress: (state, action: PayloadAction<FinalcutId>) => {
      delete state.fcStallProgress[action.payload];
    },
    deleteProcessingFc: (state, action: PayloadAction<FinalcutId>) => {
      delete state.fcProgress[action.payload];
      delete state.fcStallProgress[action.payload];
    },
    clearFinalcutQueue: (state, action: PayloadAction<FinalcutId>) => {
      delete state.fcQueue[action.payload];
    },
  },
});

export const { setFinalcutQueue, deleteProcessingFc, clearFinalcutQueue } = slice.actions;
export default slice.reducer;
